import { useEffect, useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { useR1DashboardContext } from "../../context/R1DashboardProvider";
import { fundSelectionKey, r1Api } from "../../services/api";
import { useAuth } from "@/features/Auth";
import { useValidationState } from "../../validation/hooks/useValidationState";
import type { R1Fund, ReconciliationCompanyRow } from "../../services/api";
import { buildControlsMap } from "../domain/selectors";
import type { ReconciliationCellDto } from "../data/dto";
import {
  getCanonicalFieldKey,
  makeOverrideKey,
  parseNum,
} from "../domain/status";
import { FUND_COLS, ISSUER_COLS, POSITION_COLS } from "../domain/formatters";
import type {
  AuditEntry,
  ControlEntry,
  OverrideEntry,
  Row,
} from "../domain/types";
import { sortPeriods } from "@/features/S2Dashboard/model/utils";

const PAGE_SIZE = 50;

export function useReconciliationViewModel() {
  const {
    selectedFundIds,
    selectedPeriod,
    periodsPerFund,
    funds,
    fundSelection,
  } = useR1DashboardContext();

  const [currentPage, setCurrentPage] = useState(1);

  // Reconciliation shows one fund at a time — scope all API calls to that fund only.
  const activeFundId = selectedFundIds[0];

  // Reset page when fund or period changes to avoid showing a stale empty page.
  useEffect(() => {
    setCurrentPage(1);
  }, [activeFundId, selectedPeriod]);
  const activeFundSelection: typeof fundSelection = activeFundId
    ? { mode: "ids", ids: [activeFundId] }
    : fundSelection;
  const scopeKey = fundSelectionKey(activeFundSelection);

  const { data: activePeriodsData } = useQuery({
    queryKey: ["r1", "reporting-periods-per-fund", scopeKey],
    queryFn: () => r1Api.getReportingPeriodsPerFund(activeFundSelection),
    enabled: !!activeFundId,
    staleTime: 60_000,
  });

  const activePeriodsPerFund = useMemo(() => {
    if (!activePeriodsData || !activeFundId) return periodsPerFund;
    const map = new Map<number, string[]>();
    const raw = activePeriodsData[String(activeFundId)] ?? [];
    map.set(activeFundId, sortPeriods(raw).reverse());
    return map;
  }, [activePeriodsData, activeFundId, periodsPerFund]);

  // ── Paginated companies (replaces the big positions fetch) ─────────────────
  const { data: companiesData, isLoading: companiesLoading } = useQuery({
    queryKey: [
      "r1",
      "reconciliation-companies",
      activeFundId,
      selectedPeriod,
      currentPage,
    ],
    queryFn: () =>
      r1Api.getReconciliationCompanies(
        activeFundId!,
        selectedPeriod!,
        currentPage,
        PAGE_SIZE,
      ),
    enabled: !!activeFundId && !!selectedPeriod,
    staleTime: 30_000,
  });

  const companies = useMemo<ReconciliationCompanyRow[]>(() => {
    const raw = (companiesData?.data ?? []) as ReconciliationCompanyRow[];
    const dateDesc = (a: ReconciliationCompanyRow, b: ReconciliationCompanyRow) => {
      if (!a.first_investment_date && !b.first_investment_date) return 0;
      if (!a.first_investment_date) return 1;
      if (!b.first_investment_date) return -1;
      return b.first_investment_date.localeCompare(a.first_investment_date);
    };
    const removed = raw.filter((c) => c.status === "removed").sort(dateDesc);
    const active = raw.filter((c) => c.status !== "removed").sort(dateDesc);
    return [...removed, ...active];
  }, [companiesData]);
  const companiesTotal = (companiesData?.added_count ?? 0) + (companiesData?.active_count ?? 0);
  const totalPositionsCount = companiesData?.total_positions_count ?? 0;
  // Use backend's total count (includes removed) to align pageCount with how the backend paginates.
  const pageCount = Math.max(1, Math.ceil((companiesData?.count ?? companiesTotal) / PAGE_SIZE));

  // Extract issuer IDs from current page — used to scope cells/controls fetches
  const issuerIdsCsv = useMemo(() => {
    const ids = companies
      .map((c) => Number(c.issuer_id))
      .filter((id) => Number.isFinite(id) && id > 0)
      .sort((a, b) => a - b);
    return ids.length > 0 ? ids.join(",") : undefined;
  }, [companies]);

  // ── Audit ──────────────────────────────────────────────────────────────────
  const { data: auditData, refetch: refetchAudit } = useQuery({
    queryKey: ["r1", "positions-issuers-reconciliation-audit", scopeKey],
    queryFn: () =>
      r1Api.getPositionsIssuersReconciliationAudit(activeFundId),
    enabled: selectedFundIds.length > 0,
    staleTime: 30_000,
  });

  // ── Cells & controls — scoped to current page issuers ─────────────────────
  const { data: reconciliationCellsData } = useQuery({
    queryKey: [
      "r1",
      "positions-issuers-reconciliation-cells",
      scopeKey,
      selectedPeriod ?? "all",
      issuerIdsCsv ?? "",
    ],
    queryFn: () =>
      r1Api.getPositionsIssuersReconciliationCells(
        activeFundSelection,
        selectedPeriod,
        issuerIdsCsv,
      ),
    enabled: selectedFundIds.length > 0 && !!issuerIdsCsv,
    staleTime: 30_000,
  });

  const { data: controlsHistoryData } = useQuery({
    queryKey: [
      "r1",
      "positions-issuers-reconciliation-controls-history",
      scopeKey,
      issuerIdsCsv ?? "",
    ],
    queryFn: () =>
      r1Api.getPositionsIssuersReconciliationControls(
        activeFundSelection,
        null,
        issuerIdsCsv,
      ),
    enabled: selectedFundIds.length > 0 && !!issuerIdsCsv,
    staleTime: 30_000,
  });

  // ── Issuers — only current page ───────────────────────────────────────────
  const issuerIds = useMemo(
    () =>
      companies
        .map((c) => Number(c.issuer_id))
        .filter((id) => Number.isFinite(id) && id > 0)
        .sort((a, b) => a - b),
    [companies],
  );

  const { data: issuersData } = useQuery({
    queryKey: ["r1", "issuers-by-ids", issuerIds],
    queryFn: () => r1Api.getIssuers({ ids: issuerIds }),
    staleTime: 60_000,
    enabled: issuerIds.length > 0,
  });

  const issuerMap = useMemo(() => {
    const map = new Map<number, Row>();
    for (const iss of (issuersData?.data ?? []) as Row[]) {
      map.set(Number(iss.id ?? iss.issuer_id), iss);
    }
    return map;
  }, [issuersData]);

  // ── Selected funds ─────────────────────────────────────────────────────────
  const selectedFunds = useMemo(
    () =>
      selectedFundIds
        .map((id) => funds.find((f) => Number(f.id ?? f.fund_id) === id))
        .filter((f): f is R1Fund => Boolean(f))
        .sort((a, b) =>
          String(a.fund_name || "").localeCompare(
            String(b.fund_name || ""),
            undefined,
            { sensitivity: "base" },
          ),
        ),
    [funds, selectedFundIds],
  );
  const selectedFundsById = useMemo(
    () => new Map(selectedFunds.map((f) => [Number(f.id ?? f.fund_id), f])),
    [selectedFunds],
  );

  // ── Field labels (for audit trail) ────────────────────────────────────────
  const fieldLabelByKey = useMemo(() => {
    const map = new Map<string, string>();
    for (const col of ISSUER_COLS) map.set(col.key, col.header);
    for (const col of POSITION_COLS) map.set(col.key, col.header);
    for (const col of FUND_COLS) map.set(col.key, col.header);
    return map;
  }, []);

  const auditTrail = useMemo<AuditEntry[]>(() => {
    const raw = auditData?.data ?? [];
    return raw.map((entry) => ({
      ...(function getEvent() {
        const explicitEvent = String(entry.event_label || "").toLowerCase();
        if (explicitEvent) return { eventLabel: explicitEvent };
        const c = String(entry.comment || "").toLowerCase();
        if (c.includes("false positive"))
          return { eventLabel: "false_positive" };
        if (c.includes("proposed")) return { eventLabel: "proposed" };
        return { eventLabel: "source_changed_after_approval" };
      })(),
      id: Number(entry.id),
      entityType: entry.entity_type,
      entityId: Number(entry.entity_id),
      companyName: entry.company_name || `Issuer ${entry.entity_id}`,
      fieldKey: entry.field_key || "__reconciliation_false_positive__",
      fieldLabel:
        entry.field_label ||
        fieldLabelByKey.get(entry.field_key) ||
        entry.field_key ||
        "False positive",
      previousValue: entry.previous_value || "",
      nextValue: entry.next_value || "",
      trrValue: entry.trr_value ?? undefined,
      pdfValue: entry.pdf_value ?? undefined,
      goldenValue: entry.golden_value ?? undefined,
      comment: entry.comment || "",
      user: entry.user_email || "Unknown user",
      updatedAt: entry.created_at,
    }));
  }, [auditData, fieldLabelByKey]);

  const overrides = useMemo<Record<string, OverrideEntry>>(() => {
    const latest: Record<string, OverrideEntry> = {};
    for (const item of auditTrail) {
      if (item.eventLabel === "false_positive") continue;
      const key = makeOverrideKey(
        item.entityType,
        item.entityId,
        item.fieldKey,
      );
      if (latest[key]) continue;
      latest[key] = {
        value: item.nextValue,
        comment: item.comment,
        user: item.user,
        updatedAt: item.updatedAt,
      };
    }
    return latest;
  }, [auditTrail]);

  const previousAuditValuesByKey = useMemo<Record<string, string>>(() => {
    const map: Record<string, string> = {};
    for (const item of auditTrail) {
      if (item.eventLabel === "false_positive") continue;
      const key = makeOverrideKey(
        item.entityType,
        item.entityId,
        item.fieldKey,
      );
      if (map[key] !== undefined) continue;
      map[key] = item.previousValue;
    }
    return map;
  }, [auditTrail]);

  // ── Capital account ────────────────────────────────────────────────────────
  const { data: capitalAccountData } = useQuery({
    queryKey: ["r1", "capital-account", scopeKey, selectedPeriod ?? "all"],
    queryFn: () => r1Api.getCapitalAccountBatch(activeFundSelection, selectedPeriod),
    enabled: selectedFundIds.length > 0,
    staleTime: 60_000,
  });

  const { data: rawCapitalAccountData } = useQuery({
    queryKey: [
      "r1",
      "raw-capital-account-by-fund",
      scopeKey,
      selectedPeriod ?? "",
    ],
    queryFn: () =>
      r1Api.getRawCapitalAccountByFund(activeFundSelection, selectedPeriod!),
    enabled: selectedFundIds.length > 0 && !!selectedPeriod,
    staleTime: 60_000,
  });

  // ── Controls map ───────────────────────────────────────────────────────────
  const controls = useMemo<Record<string, ControlEntry>>(() => {
    const base = buildControlsMap(
      (reconciliationCellsData?.data ??
        []) as unknown as ReconciliationCellDto[],
      selectedFundIds,
    );

    const CA_NAV_FIELDS = [
      "si_nav_before_carry_tf",
      "si_nav_before_carry_r1",
      "si_nav_after_carry_tf",
      "si_nav_after_carry_r1",
      "cq_nav_before_carry_tf",
      "cq_nav_before_carry_r1",
      "cq_nav_after_carry_tf",
      "cq_nav_after_carry_r1",
    ] as const;

    const goldenByFund = new Map<number, Record<string, unknown>>();
    for (const row of (capitalAccountData?.data ?? []) as Record<
      string,
      unknown
    >[]) {
      const fId = parseNum(row.fund_id);
      if (fId) goldenByFund.set(fId, row);
    }

    const trrByFund = new Map<number, Record<string, unknown>>();
    const pdfByFund = new Map<number, Record<string, unknown>>();
    for (const row of (rawCapitalAccountData?.data ?? []) as Record<
      string,
      unknown
    >[]) {
      const fId = parseNum(row.fund_id);
      if (!fId) continue;
      const src = String(row.source ?? "").toLowerCase();
      if (src === "trr") trrByFund.set(fId, row);
      else if (src === "pdf") pdfByFund.set(fId, row);
    }

    for (const fundId of selectedFundIds) {
      const golden = goldenByFund.get(fundId);
      const trr = trrByFund.get(fundId);
      const pdf = pdfByFund.get(fundId);
      const reportingDate = golden
        ? parseNum((golden as Record<string, unknown>).reporting_date)
        : null;

      for (const fieldKey of CA_NAV_FIELDS) {
        const goldenVal =
          golden?.[fieldKey] != null ? String(golden[fieldKey]) : undefined;
        const trrVal =
          trr?.[fieldKey] != null ? String(trr[fieldKey]) : undefined;
        const pdfVal =
          pdf?.[fieldKey] != null ? String(pdf[fieldKey]) : undefined;

        const hasAny = goldenVal || trrVal || pdfVal;
        if (!hasAny) continue;

        const nums = [goldenVal, trrVal, pdfVal]
          .filter(Boolean)
          .map((v) => parseFloat(v!));
        const allNumeric = nums.every((n) => !isNaN(n));
        const valuesPresent = [goldenVal, trrVal, pdfVal].filter(Boolean);
        const allMatch =
          valuesPresent.length > 1 &&
          (allNumeric
            ? nums.every((n) => n === nums[0])
            : valuesPresent.every((v) => v === valuesPresent[0]));
        const anyMissing = !goldenVal || !trrVal;
        const colorCode: ControlEntry["colorCode"] = allMatch
          ? "green_dark"
          : anyMissing
            ? "green_light"
            : "red";

        // Determine real status: conflict when TRR and golden both exist and differ
        const isConflict = (() => {
          if (!goldenVal || !trrVal) return false;
          const ng = Number(goldenVal);
          const nt = Number(trrVal);
          if (!isNaN(ng) && !isNaN(nt)) return ng !== nt;
          return goldenVal.trim() !== trrVal.trim();
        })();
        const status = isConflict ? "conflict" : "reviewed";

        const cellKey = `fund:${fundId}:0:${fieldKey}`;

        // If the pipeline already built a real cell (from r1_reconciliation_controls),
        // keep its status/colorCode/id — it carries false_positive, override, etc.
        // Only patch in fresh values that the pipeline cell may be missing.
        const existing = base[cellKey];
        if (existing) {
          base[cellKey] = {
            ...existing,
            trrValue: existing.trrValue ?? trrVal,
            pdfValue: existing.pdfValue ?? pdfVal,
            goldenValue: existing.goldenValue ?? goldenVal,
            displayValue: existing.displayValue ?? goldenVal ?? trrVal ?? pdfVal,
            reportingDate: existing.reportingDate ?? reportingDate,
          };
          continue;
        }

        const hasOverride = !!overrides[makeOverrideKey("fund", fundId, fieldKey)];
        const effectiveColorCode: ControlEntry["colorCode"] = hasOverride
          ? "violet"
          : colorCode;
        base[cellKey] = {
          cellKey,
          fundId,
          issuerId: null,
          positionId: null,
          status,
          fieldName: fieldKey,
          sectionName: "r1_raw_capital_account",
          level: "fund",
          entityType: "fund",
          trrValue: trrVal,
          pdfValue: pdfVal,
          goldenValue: goldenVal,
          displayValue: goldenVal ?? trrVal ?? pdfVal,
          colorCode: effectiveColorCode,
          hasOverride,
          autoMatch: false,
          reportingDate,
        };
      }
    }

    return base;
  }, [
    reconciliationCellsData,
    capitalAccountData,
    rawCapitalAccountData,
    selectedFundIds,
    overrides,
  ]);

  const controlCellKeyByTuple = useMemo<Record<string, string>>(() => {
    const byTuple: Record<string, string> = {};
    Object.entries(controls).forEach(([cellKey, control]) => {
      const fundId = control.fundId ?? 0;
      const issuerId = control.issuerId ?? 0;
      const positionId = control.positionId ?? 0;
      const reportingDate = control.reportingDate ?? 0;
      const fieldKey = getCanonicalFieldKey(control.fieldName);
      if (!fundId || !fieldKey) return;
      const tupleKey =
        control.level === "fund"
          ? `fund:${fundId}:0:0:${fieldKey}`
          : control.entityType === "position"
            ? `position:${fundId}:${positionId}:${reportingDate}:${fieldKey}`
            : `issuer:${fundId}:${issuerId}:${reportingDate}:${fieldKey}`;
      byTuple[tupleKey] = cellKey;
    });
    return byTuple;
  }, [controls]);

  const controlsHistoryByIdentity = useMemo<
    Record<string, ControlEntry[]>
  >(() => {
    const map: Record<string, ControlEntry[]> = {};
    for (const row of controlsHistoryData?.data ?? []) {
      const fundId = parseNum(row.fund_id);
      const issuerId = parseNum(row.issuer_id);
      const positionId = parseNum(row.position_id);
      const sectionName = String(row.section_name ?? "");
      const fieldName = getCanonicalFieldKey(row.field_name);
      if (!fundId || !fieldName) continue;
      const identityKey =
        sectionName === "r1_raw_fund_overview"
          ? `${fundId}::fund::${fieldName}`
          : sectionName === "r1_raw_portfolio_positions"
            ? `${fundId}::position::${positionId ?? 0}::${fieldName}`
            : `${fundId}::issuer::${issuerId ?? 0}::${fieldName}`;
      const entry: ControlEntry = {
        fundId,
        issuerId,
        positionId,
        status: String(row.reconciliation_status ?? "").toLowerCase(),
        trrValue:
          row.trr_value === null || row.trr_value === undefined
            ? undefined
            : String(row.trr_value),
        pdfValue:
          row.pdf_value === null || row.pdf_value === undefined
            ? undefined
            : String(row.pdf_value),
        goldenValue:
          row.golden_value === null || row.golden_value === undefined
            ? undefined
            : String(row.golden_value),
        reportingDate: parseNum(row.reporting_date),
      };
      if (!map[identityKey]) map[identityKey] = [];
      map[identityKey].push(entry);
    }
    Object.keys(map).forEach((k) => {
      map[k].sort((a, b) => (b.reportingDate ?? 0) - (a.reportingDate ?? 0));
    });
    return map;
  }, [controlsHistoryData]);

  const previousControlsByKey = useMemo<
    Record<string, ControlEntry | undefined>
  >(() => {
    const out: Record<string, ControlEntry | undefined> = {};
    for (const [key, control] of Object.entries(controls)) {
      const fundId = control.fundId;
      const issuerId = control.issuerId;
      const positionId = control.positionId;
      const field = getCanonicalFieldKey(control.fieldName);
      if (!fundId || !field) {
        out[key] = undefined;
        continue;
      }
      const identityKey =
        control.level === "fund"
          ? `${fundId}::fund::${field}`
          : control.entityType === "position"
            ? `${fundId}::position::${positionId ?? 0}::${field}`
            : `${fundId}::issuer::${issuerId ?? 0}::${field}`;
      const series = controlsHistoryByIdentity[identityKey] ?? [];
      const prev = series.find((v) =>
        control.reportingDate
          ? (v.reportingDate ?? 0) < control.reportingDate
          : true,
      );
      out[key] = prev;
    }
    return out;
  }, [controls, controlsHistoryByIdentity]);

  const controlsByFundIssuer = useMemo(() => {
    const map = new Map<string, ControlEntry[]>();
    Object.values(controls).forEach((entry) => {
      const fund = String(entry.fundName ?? "").toLowerCase();
      const issuer = String(entry.issuerName ?? "").toLowerCase();
      if (!fund || !issuer) return;
      const key = `${fund}::${issuer}`;
      if (!map.has(key)) map.set(key, []);
      map.get(key)!.push(entry);
    });
    return map;
  }, [controls]);

  const controlCompaniesByFund = useMemo(() => {
    const map = new Map<number, string[]>();
    for (const [k] of controlsByFundIssuer) {
      const [fundName, issuerName] = k.split("::");
      const fundId = selectedFunds.find(
        (f) => String(f.fund_name || "").toLowerCase() === fundName,
      )?.id;
      if (!fundId || !issuerName || issuerName === fundName) continue;
      const numId = Number(fundId);
      if (!map.has(numId)) map.set(numId, []);
      const arr = map.get(numId)!;
      if (!arr.includes(issuerName)) arr.push(issuerName);
    }
    return map;
  }, [controlsByFundIssuer, selectedFunds]);

  // ── Delta summary — from current page ─────────────────────────────────────
  const companyDeltaSummary = useMemo(() => ({
    addedCount: companiesData?.added_count ?? 0,
    removedCount: companiesData?.removed_count ?? 0,
    pdfRawCount: companiesData?.pdf_raw_count ?? null,
    trrRawCount: companiesData?.trr_raw_count ?? null,
  }), [companiesData]);

  const colorSummary = useMemo(() => {
    let red = 0,
      orange = 0,
      greenDark = 0,
      greenLight = 0,
      manual = 0;
    for (const c of Object.values(controls)) {
      switch (c.colorCode) {
        case "red":
          red++;
          break;
        case "orange_light":
          orange++;
          break;
        case "green_dark":
          greenDark++;
          break;
        case "green_light":
          greenLight++;
          break;
        case "violet":
          manual++;
          break;
      }
    }
    return { red, orange, greenDark, greenLight, manual };
  }, [controls]);

  const selectedPeriodLabel = selectedPeriod ?? "All periods";
  const hasReconciliationControls = Object.keys(controls).length > 0;
  const isEffectivelyEmpty =
    !companiesLoading && companies.length === 0 && !hasReconciliationControls;
  const isLoading = companiesLoading;

  const { user } = useAuth();
  const { quantiliaLocked, r1Locked } = useValidationState(
    activeFundId!,
    selectedPeriod!,
    !!activeFundId && !!selectedPeriod,
  );

  const canEdit = useMemo(() => {
    if (!user) return false;
    const groups = user.groups ?? [];
    if (groups.includes("r1-quantilia") && quantiliaLocked) return false;
    if (groups.includes("r1-users") && r1Locked) return false;
    return true;
  }, [user, quantiliaLocked, r1Locked]);

  const editLockTooltip = useMemo(() => {
    if (!user) return undefined;
    const groups = user.groups ?? [];
    if (groups.includes("r1-quantilia") && quantiliaLocked)
      return "Validated by Quantilia — reject step 1 to unlock";
    if (groups.includes("r1-users") && r1Locked)
      return "Validated by R1 — reject step 3 to unlock";
    return undefined;
  }, [user, quantiliaLocked, r1Locked]);

  return {
    selectedFundIds,
    selectedPeriod,
    periodsPerFund: activePeriodsPerFund,
    selectedFunds,
    selectedFundsById,
    issuerMap,
    auditTrail,
    overrides,
    previousAuditValuesByKey,
    controls,
    controlCellKeyByTuple,
    previousControlsByKey,
    controlsHistoryByIdentity,
    controlsByFundIssuer,
    controlCompaniesByFund,
    companies,
    companiesTotal,
    totalPositionsCount,
    currentPage,
    setCurrentPage,
    pageCount,
    companyDeltaSummary,
    colorSummary,
    selectedPeriodLabel,
    isEffectivelyEmpty,
    isLoading,
    refetchAudit,
    canEdit,
    editLockTooltip,
  };
}
